import { ACCESS_TYPE } from "@/helpers/constants";
import { Contact } from "@/models/contact";
import { defineStore } from "pinia";
import { computed, ComputedRef, ref } from "vue";
import { map, forEach } from "underscore";
import { contactService } from "@/services/contacts.service";
import { AccessType } from "@/models/smart-group-item";

export interface ContactModulesState {
  favContacts: Contact[];
  isContactLoading: boolean;
  contactsHasError: boolean;
  defaultContactFolder: string;
  defaultContactFolderDetails: ContactFolder;
  contactsList: Contact[];
  metaData: ContactMetaData;
  contactsColumns: object;
  uploadedFileId: string;
}
export interface ContactFolder {
  contactFolderId?: string;
  alias?: string;
  ownerUserId?: number;
  name?: string;
  createdDate?: string; // ISO 8601 date string
  contactFolderAccessId?: string;
  userId?: number;
  accessType?: AccessType.READ_WRITE | AccessType.READ_ONLY; // Assuming these are the only options
  headerMappingsJson?: {
    [key: string]: string; // Mapping of header names to field keys
  };
}
export interface ContactMetaData {
  [key: string]: {
    fieldKey: string;
    fieldName: string;
    customField: boolean;
    dataType: string;
    viewType: string;
    required: boolean;
    unique: boolean;
    updatable: boolean;
    minLength: number;
    maxLength: number;
    enumValues?: string[]; // Optional, for ARRAY types
    dateTimeFormat?: string; // Optional, for DATE types
    id?: string; // Optional, for custom fields with an ID
  };
}

const sortAscending = (a: any, b: any) => {
  if (a.label < b.label) {
    return -1;
  }
  if (a.label > b.label) {
    return 1;
  }
  return 0;
};

export const useContactsStore = defineStore("contacts", () => {
  // STATES
  const favContacts = ref<Contact[]>([]);
  const isContactLoading = ref<boolean>(false);
  const contactsHasError = ref<boolean>(false);
  const defaultContactFolder = ref<string>("");
  const defaultContactFolderDetails = ref<ContactFolder>({});
  const contactsList = ref<Contact[]>([]);
  const metaData = ref<ContactMetaData>({});
  const uploadedFileId = ref<string>("");

  const fetchFavourites = async (folderId: string) => {
    isContactLoading.value = true;
    await contactService
      .getFilteredContacts(0, 25, folderId, "favourite:1", "firstName")
      .then(({ data }) => {
        if (data.content !== null) {
          favContacts.value = data.content;
        } else {
          favContacts.value = [];
        }
      })
      .catch(() => {
        contactsHasError.value = true;
        return [];
      })
      .finally(() => {
        isContactLoading.value = false;
      });
  };
  const setDefaultFolder = (id: string) => {
    defaultContactFolder.value = id;
  };
  const setDefaultFolderDetails = (payload: any) => {
    defaultContactFolderDetails.value = payload;
  };
  const setContactsList = async (folderId: string) => {
    const { data } = await contactService.getContacts(
      0,
      25,
      folderId || defaultContactFolder.value,
      "",
      "firstName",
    );
    contactsList.value = data.content;
  };

  const getFavContacts: ComputedRef<Contact[]> = computed(
    () => favContacts.value,
  );

  const getUploadFileId: ComputedRef<string> = computed(
    () => uploadedFileId.value,
  );

  const setMetaData = async (id: string) => {
    const { data }: any = await contactService.getMetaData(id);
    metaData.value = data ? data.metadataColumnViewMap : {};
  };

  const setUploadedFileId = (payload: string = "") => {
    uploadedFileId.value = payload;
  };

  const setFavContacts = (contacts: Contact[]) => {
    favContacts.value = contacts;
  };

  // GETTERS
  const getDefaultContactFolder: ComputedRef<string> = computed(
    () => defaultContactFolder.value,
  );
  const getDefaultContactFolderDetail: ComputedRef<ContactFolder> = computed(
    () => defaultContactFolderDetails.value ?? {},
  );
  const getWriteAccess: ComputedRef<boolean> = computed(
    () =>
      (defaultContactFolderDetails.value?.accessType as
        | ACCESS_TYPE
        | undefined) === ACCESS_TYPE.READ_WRITE,
  );
  const getMetaDataObject: ComputedRef<ContactMetaData> = computed(
    () => metaData.value,
  );
  const getMetaDataList: ComputedRef<any[]> = computed(() => {
    const list: any[] = [];
    if (metaData.value) {
      forEach(Object.keys(metaData.value), (field: string) => {
        list.push({
          label: metaData.value[field].fieldName,
          value: metaData.value[field].fieldKey,
          meta: {
            ...metaData.value[field],
            enumValues: metaData.value[field].enumValues
              ? map(metaData.value[field].enumValues, (enums: string) => ({
                  label: enums,
                  value: enums,
                }))
              : [],
          },
        });
      });
    }
    return list.sort(sortAscending);
  });

  return {
    getDefaultContactFolder,
    getDefaultContactFolderDetail,
    getWriteAccess,
    getMetaDataObject,
    getMetaDataList,
    getFavContacts,
    getUploadFileId,
    setFavContacts,
    setMetaData,
    fetchFavourites,
    setDefaultFolder,
    setDefaultFolderDetails,
    setContactsList,
    setUploadedFileId,
  };
});
